import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import api, { formatApiError } from "../api/client";
import { CheckCircle, Clock, XCircle, Loader2 } from "lucide-react";

const MAX_ATTEMPTS = 8;
const INTERVAL_MS = 2000;

export default function PaymentReturn() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | expired | failed | error
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState("");
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      setError(t("payment.error.missing"));
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setPayload(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus("failed");
          return;
        }
        setTimeout(poll, INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        setError(formatApiError(e.response?.data?.detail) || e.message);
        setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen pt-24 pb-20 bg-[var(--bg)]" data-testid="payment-return-page">
      <div className="max-w-xl mx-auto px-6 md:px-10">
        <div className="card-soft p-8 text-center">
          {status === "checking" && (
            <>
              <Loader2 size={36} strokeWidth={1.2} className="mx-auto text-[color:var(--accent)] animate-spin" />
              <h2 className="font-serif text-2xl text-[color:var(--ink)] mt-4">{t("payment.checking.title")}</h2>
              <p className="mt-3 text-[color:var(--ink-2)]">{t("payment.checking.body")}</p>
            </>
          )}
          {status === "paid" && (
            <>
              <CheckCircle size={40} strokeWidth={1.2} className="mx-auto text-[color:var(--accent)]" />
              <h2 className="font-serif text-3xl text-[color:var(--ink)] mt-4">{t("payment.paid.title")}</h2>
              <p className="mt-3 text-[color:var(--ink-2)] leading-relaxed">
                {t("payment.paid.body")}
              </p>
              {payload && (
                <p className="text-xs text-[color:var(--ink-3)] mt-4">
                  Paid {payload.amount_total ? (payload.amount_total / 100).toFixed(2) : ""} {(payload.currency || "").toUpperCase()}
                </p>
              )}
              <div className="mt-7 flex justify-center gap-3">
                <Link to="/" className="btn-ghost" data-testid="payment-return-home">{t("register.pending.back")}</Link>
                <Link to="/login" className="btn-primary" data-testid="payment-return-login">{t("register.pending.signin")}</Link>
              </div>
            </>
          )}
          {status === "expired" && (
            <>
              <Clock size={36} strokeWidth={1.2} className="mx-auto text-[color:var(--accent-2)]" />
              <h2 className="font-serif text-2xl text-[color:var(--ink)] mt-4">{t("payment.expired.title")}</h2>
              <p className="mt-3 text-[color:var(--ink-2)]">{t("payment.expired.body")}</p>
              <Link to="/register" className="btn-primary mt-6">{t("payment.expired.cta")}</Link>
            </>
          )}
          {status === "failed" && (
            <>
              <Clock size={36} strokeWidth={1.2} className="mx-auto text-[color:var(--accent-2)]" />
              <h2 className="font-serif text-2xl text-[color:var(--ink)] mt-4">{t("payment.failed.title")}</h2>
              <p className="mt-3 text-[color:var(--ink-2)]">{t("payment.failed.body")}</p>
              <div className="mt-6 flex justify-center gap-3">
                <Link to="/" className="btn-ghost">{t("register.pending.back")}</Link>
                <Link to="/login" className="btn-primary">{t("register.pending.signin")}</Link>
              </div>
            </>
          )}
          {status === "error" && (
            <>
              <XCircle size={36} strokeWidth={1.2} className="mx-auto text-[color:var(--accent-2)]" />
              <h2 className="font-serif text-2xl text-[color:var(--ink)] mt-4">{t("payment.error.title")}</h2>
              <p className="mt-3 text-[color:var(--ink-2)]">{error || t("payment.error.fallback")}</p>
              <Link to="/register" className="btn-primary mt-6">{t("payment.error.cta")}</Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
